import { IntlShape } from 'react-intl'

import { StepEnum } from '../modal-select-proposal.enum'
import { ISelectProposalForm } from '../modal-select-proposal.types'
import { FormClientPricingMenusOutputDtoRecord } from '../modal-select-proposal-menus/ModalSelectProposalMenus'
import { getLogsMenuItemName } from '../modal-select-proposal-menus/select-proposal-menu-item/utils'
import { SearchDateAdultsModeEnum } from '../../search-dates-adults/search-dates-adults.enum'
import {
  ArrivalEnum,
  ClientPricingItemInputDto,
  ClientPricingMenuOutputDto,
  ClientPricingMenuTypeEnum,
  DepartureEnum,
  HousePriceTypeEnum,
  HousesInformationsFieldsFragment,
  TravelTypeEnum,
} from '../../../../lib/graphql'
import { DateFormatter } from '../../../../utils/DateFormatter'
import { errorToast } from '../../../../utils/toaster/toaster'
import { EnvironmentTypeEnum } from '../../../contexts'

export const isAutonomousProposalInputValid = ({
  values,
  travelType,
  priceType,
  isAutonomousBookingAvailable,
  intl,
}: {
  values: ISelectProposalForm
  travelType: TravelTypeEnum
  priceType: HousePriceTypeEnum
  isAutonomousBookingAvailable: boolean
  intl: IntlShape
}): boolean => {
  const errors = []
  if (!values.adults) {
    errors.push(
      intl.formatMessage({
        id: 'ui.isAutonomousProposalInputValid.adultNumberNotFilled',
        defaultMessage: "Veuillez renseigner le nombre d'adultes",
      }),
    )
  }
  if (!values.dates?.startDate) {
    errors.push(
      intl.formatMessage({
        id: 'ui.isAutonomousProposalInputValid.startDateNotFilled',
        defaultMessage: 'Veuillez renseigner la date de début',
      }),
    )
  }
  if (travelType === TravelTypeEnum.NIGHTLY_TRIP && !values.dates?.endDate) {
    errors.push(
      intl.formatMessage({
        id: 'ui.isAutonomousProposalInputValid.endDateNotFilled',
        defaultMessage: 'Veuillez renseigner la date de fin',
      }),
    )
  }
  if (
    travelType === TravelTypeEnum.NIGHTLY_TRIP &&
    priceType === HousePriceTypeEnum.INDIVIDUAL &&
    !values.individualBedroomAdults &&
    !values.sharedBedroomAdults
  ) {
    errors.push(
      intl.formatMessage({
        id: 'ui.isAutonomousProposalInputValid.bedroomsNumberNotFilled',
        defaultMessage: 'Veuillez renseigner le nombre de chambres',
      }),
    )
  }
  if (!values.briefTitle && !values.clientRequestId) {
    errors.push(
      intl.formatMessage({
        id: 'ui.isAutonomousProposalInputValid.projectNotSelected',
        defaultMessage: 'Veuillez sélectionner un projet',
      }),
    )
  }

  if (
    travelType !== TravelTypeEnum.CORPORATE_EVENING_EVENT &&
    isAutonomousBookingAvailable &&
    !Object.values(values.menus).some((menu) => menu.quantity > 0)
  ) {
    errors.push(
      intl.formatMessage({
        id: 'ui.isAutonomousProposalInputValid.menuNotSelected',
        defaultMessage: 'Veuillez sélectionner une formule',
      }),
    )
  }

  if (errors.length > 0) {
    errorToast(errors)
  }
  return errors.length === 0 ? true : false
}

export const formatClientPricingItemsFromMenus = (
  formValues: ISelectProposalForm,
): ClientPricingItemInputDto[] => {
  if (!formValues.menus) {
    return []
  }

  const clientPricingItems: ClientPricingItemInputDto[] = []

  Object.values(formValues.menus).forEach((menu) => {
    const priceItems = menu.unitMenu.priceItems ?? []

    priceItems.forEach((priceItem) => {
      const existingClientPricingItemIndex = clientPricingItems.findIndex(
        (clientPricingItem) =>
          clientPricingItem.type === priceItem.type &&
          clientPricingItem.option?.objectId === priceItem.option.objectId,
      )

      if (existingClientPricingItemIndex !== -1) {
        clientPricingItems[
          existingClientPricingItemIndex
        ].priceOption.quantity += menu.quantity * priceItem.priceOption.quantity
      } else {
        clientPricingItems.push({
          _id: priceItem._id,
          category: priceItem.category,
          type: priceItem.type,
          unitPrice: {
            basePrice: priceItem.unitPrice?.basePrice,
          },
          priceOption: {
            ...priceItem.priceOption,
            quantity: menu.quantity * priceItem.priceOption.quantity,
          },
          option: priceItem.option,
          objectData: {
            ...priceItem.objectData,
            label: priceItem.objectData?.label ?? '',
            prices:
              priceItem.objectData?.prices.map(
                ({ price: _price, ...restPrice }) => ({
                  ...restPrice,
                }),
              ) ?? [],
          },
        })
      }
    })
  })

  return clientPricingItems
}

export const isFormValid = ({
  house,
  form,
  page,
  travelType,
}: {
  form: ISelectProposalForm
  house: HousesInformationsFieldsFragment
  page: StepEnum
  travelType: TravelTypeEnum
}): boolean => {
  switch (page) {
    case StepEnum.SELECT_OPTIONS:
      if (!form.dates.startDate || !form.dates.endDate) {
        return false
      }

      if (!form.adults) {
        return false
      }

      if (
        house.priceType === HousePriceTypeEnum.INDIVIDUAL &&
        travelType === TravelTypeEnum.NIGHTLY_TRIP
      ) {
        if (!form.individualBedroomAdults && !form.sharedBedroomAdults) {
          return false
        }
      }
      break
    case StepEnum.SELECT_MENU:
      if (!Object.values(form.menus).some((menu) => menu.quantity > 0)) {
        return false
      }
      break
    case StepEnum.SELECT_CLIENT_REQUEST:
      if (!form.clientRequestId && !form.briefTitle) {
        return false
      }
      break
  }

  return true
}

export const computeFormulaLogMessage = ({
  menus,
}: {
  menus: ClientPricingMenuOutputDto[]
}): string[] => {
  const computedMenus: string[] = []

  for (const menu of menus) {
    const { quantity } = menu

    if (quantity > 0) {
      computedMenus.push(
        `Formule choisie x${quantity} : ${getLogsMenuItemName(menu)}`,
      )
    }
  }

  return computedMenus
}

function formatArrival(arrival?: ArrivalEnum): string {
  let arrivalLabel: string

  switch (arrival) {
    case ArrivalEnum.MORNING:
      arrivalLabel = 'Matin'
      break
    case ArrivalEnum.BEFORE_LUNCH:
      arrivalLabel = 'Avant-midi'
      break
    case ArrivalEnum.EVENING:
      arrivalLabel = 'Soir'
      break
    default:
      arrivalLabel = 'Inconnu'
  }

  return `Arrivée: ${arrivalLabel}`
}

function formatDeparture(departure?: DepartureEnum): string {
  let departureLabel: string

  switch (departure) {
    case DepartureEnum.MORNING:
      departureLabel = 'Matin'
      break
    case DepartureEnum.AFTER_LUNCH:
      departureLabel = 'Après-midi'
      break
    case DepartureEnum.EVENING:
      departureLabel = 'Soir'
      break
    default:
      departureLabel = 'Inconnu'
  }

  return `Départ: ${departureLabel}`
}

export const computeOptionsLogMessage = (
  formValues: ISelectProposalForm,
  dateFormatter: DateFormatter,
  travelType: TravelTypeEnum,
): string[] => {
  const {
    restauration,
    sharedBedroomAdults,
    individualBedroomAdults,
    arrival,
    departure,
    adults,
    dates: { startDate, endDate },
  } = formValues

  const computedLogMessage = [
    `Type de séjour : ${translateTravelType({ travelType })}`,
    ...(adults ? [`Nombre de participants: ${adults}`] : []),
    ...(travelType !== TravelTypeEnum.CORPORATE_EVENING_EVENT
      ? [
          `Format restauration: ${restauration ? 'pension complète' : 'location sèche'}`,
        ]
      : []),
    `Arrivée: ${formatArrival(arrival)}`,
    `Départ: ${formatDeparture(departure)}`,
    ...(adults ? [`Nombre de participants : ${adults}`] : []),
    ...(sharedBedroomAdults
      ? [`Participant en chambre twin: ${sharedBedroomAdults}`]
      : []),
    ...(individualBedroomAdults
      ? [`Participant en chambre single: ${individualBedroomAdults}`]
      : []),
    ...(startDate && endDate
      ? [
          `Dates choisies: ${dateFormatter.formatDateRange({
            startDate,
            endDate,
            formatString: 'DD/MM/YY',
            useUTC: true,
          })}`,
        ]
      : []),
  ]

  return computedLogMessage
}

export const isHouseCardAndAutonomous = ({
  mode,
  isAutonomousBookingAvailable,
}: {
  mode: SearchDateAdultsModeEnum
  isAutonomousBookingAvailable: boolean
}) => {
  if (
    mode === SearchDateAdultsModeEnum.IN_HOUSE_CARD_AUTONOMOUS_BOOKING &&
    isAutonomousBookingAvailable
  ) {
    return true
  }
  return false
}

export const getSelectProposalPageTitle = ({
  page,
  environment,
  intl,
}: {
  page: StepEnum
  environment: EnvironmentTypeEnum | undefined
  intl: IntlShape
}): string => {
  switch (page) {
    case StepEnum.SELECT_OPTIONS:
      return intl.formatMessage({
        id: 'ui.getSelectProposalPageTitle.selectOptions',
        defaultMessage: 'Sélectionnez vos options',
      })
    case StepEnum.SELECT_MENU:
      return intl.formatMessage({
        id: 'ui.getSelectProposalPageTitle.selectMenu',
        defaultMessage: 'Les formules disponibles',
      })
    case StepEnum.SELECT_EXTRA:
      return intl.formatMessage({
        id: 'ui.getSelectProposalPageTitle.selectExtra',
        defaultMessage: 'Les suppléments du lieu',
      })
    case StepEnum.SELECT_CLIENT_REQUEST:
      if (environment === EnvironmentTypeEnum.BACKOFFICE) {
        return intl.formatMessage({
          id: 'ui.getSelectProposalPageTitle.addQuoteToTransaction',
          defaultMessage: 'Ajouter ce devis à une transaction',
        })
      } else {
        return intl.formatMessage({
          id: 'ui.getSelectProposalPageTitle.selectProject',
          defaultMessage: 'Sélectionnez un projet',
        })
      }
  }
}

export const getHouseRegularMenus = ({
  formValues,
}: {
  formValues: ISelectProposalForm
}): ClientPricingMenuOutputDto[] => {
  return Object.values(formValues.menus).filter(
    (menu) =>
      menu.type === ClientPricingMenuTypeEnum.NIGHT_MENU ||
      menu.type === ClientPricingMenuTypeEnum.DAY_MENU ||
      menu.type === ClientPricingMenuTypeEnum.WORKSPACE_MENU ||
      menu.type === ClientPricingMenuTypeEnum.HOUSE_MENU,
  )
}

export const getHouseExtrasMenus = ({
  formValues,
}: {
  formValues: ISelectProposalForm
}): ClientPricingMenuOutputDto[] => {
  return Object.values(formValues.menus).filter(
    (menu) =>
      menu.type === ClientPricingMenuTypeEnum.WORKSPACE_MENU ||
      menu.type === ClientPricingMenuTypeEnum.EVENING_MENU,
  )
}

export const hasHouseExtrasMenus = ({
  formValues,
}: {
  formValues: ISelectProposalForm
}): boolean => {
  return getHouseExtrasMenus({ formValues }).length > 0
}

export const hasMenus = ({
  menus,
}: {
  menus: FormClientPricingMenusOutputDtoRecord
}): boolean => {
  return Object.keys(menus).length > 0
}

export const hasHouseEveningMenu = ({
  formValues,
}: {
  formValues: ISelectProposalForm
}): boolean => {
  return (
    Object.values(formValues.menus).filter(
      (menu) => menu.type === ClientPricingMenuTypeEnum.EVENING_MENU,
    ).length > 0
  )
}

const translateTravelType = ({
  travelType,
}: {
  travelType: TravelTypeEnum
}): string => {
  switch (travelType) {
    case TravelTypeEnum.CORPORATE_EVENING_EVENT:
      return "Soirée d'entreprise 🎉"
    case TravelTypeEnum.DAILY_TRIP:
      return "Journée d'étude ☀️"
    case TravelTypeEnum.NIGHTLY_TRIP:
      return 'Séminaire résidentiel 🌙'
    default:
      return 'Type de séjour inconnu'
  }
}
